import {
  AppBar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Menu as MenuIcon } from "lucide-react";
import { useId, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../hooks/useTenant";
import { logout, UserRole } from "../services/authSlice";
import { stopImpersonation } from "../services/impersonationSlice";
import { RootState } from "../store";
import { hasRole } from "../utils/roleHelpers";
import DarkModeToggle from "./DarkModeToggle";

interface NavigationItem {
  label: string;
  to: string;
  roles: UserRole[];
}

type NavBarProps = object;

const LogoutButton = styled(Button)(({ theme }) => ({
  marginLeft: "auto",
  color: theme.palette.error.main,
  "&:hover": {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.main,
  },
}));

const BrandLink = styled(RouterLink)(({ theme }) => ({
  textDecoration: "none",
  "&:hover": {
    "& .brandText": {
      textDecoration: `underline 2px ${theme.palette.primary.main}`,
      textUnderlineOffset: "4px",
    },
  },
  "& .brandText": {
    typography: "h5",
    fontWeight: 700,
    color: theme.palette.primary.main,
    letterSpacing: "0.5px",
    transition: "text-decoration 0.2s ease",
  },
}));

// Helper functions for navigation logic
const getBrandText = (
  userRoles: UserRole[] | undefined,
  tenantName?: string | null,
): string => {
  if (hasRole(userRoles, "developer") || !tenantName) {
    return "Monulink";
  }
  return tenantName;
};

const getBrandLink = (
  userRoles: UserRole[] | undefined,
  currentTenantSlug?: string,
): string => {
  if (hasRole(userRoles, "developer") || hasRole(userRoles, "tenant-manager")) {
    return "/tenant-manager/tenants";
  }
  if (currentTenantSlug) {
    return `/${currentTenantSlug}/orders`;
  }
  return "/orders";
};

const getNavigationItems = (
  userRoles: UserRole[] | undefined,
  currentTenantSlug?: string,
): NavigationItem[] => {
  const items: NavigationItem[] = [];

  // Tenant owner orders
  if (hasRole(userRoles, "tenant-owner") && currentTenantSlug) {
    items.push({
      label: "Orders",
      to: `/${currentTenantSlug}/orders`,
      roles: ["tenant-owner"],
    });
    items.push({
      label: "Branding",
      to: `/${currentTenantSlug}/branding`,
      roles: ["tenant-owner"],
    });
    items.push({
      label: "Customer Management",
      to: `/${currentTenantSlug}/customer-management`,
      roles: ["tenant-owner"],
    });
    items.push({
      label: "Cemetery Management",
      to: `/${currentTenantSlug}/cemetery-management`,
      roles: ["tenant-owner"],
    });
    items.push({
      label: "Settings",
      to: `/${currentTenantSlug}/settings`,
      roles: ["developer", "tenant-owner"],
    });
  }

  // Tenant management (both tenant-manager and developer roles)
  if (hasRole(userRoles, "tenant-manager") || hasRole(userRoles, "developer")) {
    items.push({
      label: "Tenants",
      to: "/tenant-manager/tenants",
      roles: ["tenant-manager", "developer"],
    });
  }

  // Developer-specific tools
  if (hasRole(userRoles, "developer")) {
    items.push({
      label: "Cemetery Management",
      to: "/developer/cemetery-management",
      roles: ["developer"],
    });
  }

  // Customer orders
  if (hasRole(userRoles, "customer")) {
    items.push({
      label: "Orders",
      to: currentTenantSlug ? `/${currentTenantSlug}/orders` : "/orders",
      roles: ["customer"],
    });
  }

  return items;
};

const getUserActionItems = (
  userRoles: UserRole[] | undefined,
): NavigationItem[] => {
  const items: NavigationItem[] = [];

  if (hasRole(userRoles, "customer")) {
    items.push({
      label: "Edit Profile",
      to: "/edit-profile",
      roles: ["customer"],
    });
  }

  return items;
};

const NavBar: React.FC<NavBarProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const impersonation = useSelector((state: RootState) => state.impersonation);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const mobileMenuId = useId();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  const { tenantName, currentTenantSlug } = useTenant();

  const navigationItems = getNavigationItems(user?.roles, currentTenantSlug);
  const userActionItems = getUserActionItems(user?.roles);

  const handleLogout = () => {
    // if currently impersonating, clear impersonation state too
    if (impersonation.isImpersonating) {
      dispatch(stopImpersonation());
    }
    dispatch(logout());
    navigate("/auth");
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  return (
    <AppBar position="static" color="default" elevation={1}>
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        <BrandLink to={getBrandLink(user?.roles, currentTenantSlug)}>
          <Box className="brandText">
            {getBrandText(user?.roles, tenantName)}
          </Box>
        </BrandLink>

        {!isMobile && (
          <>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {navigationItems.map((item) => (
                <Button
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  color="primary"
                  sx={{
                    fontWeight: location.pathname === item.to ? 700 : 400,
                    borderBottom:
                      location.pathname === item.to
                        ? `2px solid ${theme.palette.primary.main}`
                        : "none",
                    borderRadius: 0,
                    pb: location.pathname === item.to ? 0.5 : 0.75,
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              {userActionItems.map((item) => (
                <Button
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  color="primary"
                  sx={{
                    fontWeight: location.pathname === item.to ? 700 : 400,
                    borderBottom:
                      location.pathname === item.to
                        ? `2px solid ${theme.palette.primary.main}`
                        : "none",
                    borderRadius: 0,
                    pb: location.pathname === item.to ? 0.5 : 0.75,
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <DarkModeToggle />
              <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
            </Stack>
          </>
        )}

        {isMobile && (
          <Box sx={{ marginLeft: "auto" }}>
            <IconButton
              size="large"
              edge="end"
              color="primary"
              onClick={handleMobileMenuOpen}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id={mobileMenuId}
              anchorEl={mobileMenuAnchor}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
            >
              {navigationItems.map((item) => (
                <MenuItem
                  key={item.to}
                  onClick={() => {
                    navigate(item.to);
                    handleMobileMenuClose();
                  }}
                  sx={{
                    fontWeight: location.pathname === item.to ? 700 : 400,
                    backgroundColor:
                      location.pathname === item.to
                        ? theme.palette.action.selected
                        : "transparent",
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
              {userActionItems.map((item) => (
                <MenuItem
                  key={item.to}
                  onClick={() => {
                    navigate(item.to);
                    handleMobileMenuClose();
                  }}
                  sx={{
                    fontWeight: location.pathname === item.to ? 700 : 400,
                    backgroundColor:
                      location.pathname === item.to
                        ? theme.palette.action.selected
                        : "transparent",
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
              <MenuItem sx={{ justifyContent: "center", py: 1 }}>
                <DarkModeToggle size="small" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleLogout();
                  handleMobileMenuClose();
                }}
              >
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>
    </AppBar>
  );
};

export default NavBar;
